// src/components/DpcBranchSelector.tsx
// [2026-07-18 ユーザ指示] DPC手術分類(10桁)/処置1分類(11桁)/処置2分類(12桁) の独立セレクタ。
// DiseaseSelector (DPC疾患名/コード) と同じ作法:
//   - datalist で名称検索 (候補 = dim_dpc10/11/12 = fact に実在する分岐のみ)
//   - 上位の選択 (疾患→手術分類→処置1分類) があれば候補をその配下に絞る
//   - 主表の行クリックで飛んできた値も input に同期表示・× でクリア (下位も連鎖クリア)
//   - セレクタからの選択では階層タブは遷移しない (自動遷移は行クリックのみ)
// DPC分析タブと粗利分析タブで共通使用。
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useFilters, dpcChainPatch } from "../store";
import type { DpcBranchListResponse } from "@shared/api";
import { GP_MODE_HEADER } from "../lib/gpMode";

const LEVEL_META = {
  10: { key: "d10", digits: 10, placeholder: "例: 経皮的冠動脈形成 / 050030xx03" },
  11: { key: "d11", digits: 11, placeholder: "例: 5あり / 050030xx035" },
  12: { key: "d12", digits: 12, placeholder: "例: 1あり / 050030xx0301" },
} as const;

function useDpcBranchList(level: 10 | 11 | 12, under: string | null) {
  return useQuery({
    queryKey: ["dpc_branch_list", level, under],
    queryFn: async () => {
      const p = new URLSearchParams({ level: String(level) });
      if (under) p.set("under", under);
      const r = await fetch(`/api/query/dpc_branch_list?${p.toString()}`, { credentials: "include", headers: { ...GP_MODE_HEADER } });
      if (!r.ok) throw new Error(`dpc_branch_list ${r.status}`);
      return r.json() as Promise<DpcBranchListResponse>;
    },
    placeholderData: (prev) => prev,
    staleTime: 5 * 60_000,
  });
}

export function DpcBranchSelector({ level }: { level: 10 | 11 | 12 }) {
  const f = useFilters();
  const meta = LEVEL_META[level];

  // 現在の選択値 (store) と、候補を絞る上位コード
  const selected =
    level === 10 ? f.selectedDpc10 :
    level === 11 ? f.selectedDpc11 : f.selectedDpc12;
  const under =
    level === 10 ? (f.selectedDpc8 ?? f.selectedDpc7 ?? f.dpc6) :
    level === 11 ? (f.selectedDpc10 ?? f.dpc6) :
    (f.selectedDpc11 ?? f.selectedDpc10 ?? f.dpc6);

  const { data, isLoading } = useDpcBranchList(level, under);
  const rows = data?.rows ?? [];
  const current = selected ? rows.find((r) => r.code === selected) : undefined;

  // DiseaseSelector と同じ local-input パターン (入力途中の文字を消さない)
  const [input, setInput] = useState<string>("");
  useEffect(() => {
    if (selected) setInput(current ? `${current.code} ${current.name}` : selected);
    else setInput("");
  }, [selected, current]);

  // 選択確定: コードから prefix チェーン (dpc6+d10+d11+d12) を丸ごとセット。
  // 深い側は null クリア。階層タブ (dpcLevel) は動かさない。
  const applySelect = (code: string) => {
    useFilters.setState({
      dpc6: code.slice(0, 6),
      ...dpcChainPatch(code, level),   // 7/8 桁目 (x 以外) も prefix で埋める
    });
  };
  const clear = () => {
    if (level === 10) f.setSelectedDpc10(null);
    else if (level === 11) f.setSelectedDpc11(null);
    else f.setSelectedDpc12(null);
  };

  // 表示階層より深い絞り込みは主表に未適用 (下位を上位に反映させないゲート)
  const inactive = selected != null && f.dpcLevel < level;
  const listId = `dpc-branch-${level}-options`;

  return (
    <>
      <div className="flex items-center gap-1">
        <input
          list={listId}
          type="text"
          placeholder={meta.placeholder}
          className={`flex-1 min-w-0 border border-slate-300 rounded px-2 py-1 bg-white ${inactive ? "opacity-60" : ""}`}
          value={input}
          disabled={isLoading && rows.length === 0}
          onChange={(e) => {
            const v = e.target.value;
            setInput(v);
            const trimmed = v.trim();
            if (trimmed === "") {
              if (selected !== null) clear();
              return;
            }
            const m = trimmed.match(new RegExp(`^([0-9xX]{${meta.digits}})(?![0-9xX])`));
            if (m && m[1].toLowerCase() !== selected) applySelect(m[1].toLowerCase());
          }}
        />
        {selected && (
          <button
            type="button"
            onClick={() => { clear(); setInput(""); }}
            className="text-[10px] px-1 text-red-600 hover:underline"
            title="クリア (下位の絞り込みも解除)"
          >×</button>
        )}
      </div>
      <datalist id={listId}>
        {rows.map((r) => (
          <option key={r.code} value={`${r.code} ${r.name}`} />
        ))}
      </datalist>
      {inactive && (
        <div className="text-[9px] text-slate-400 mt-0.5 leading-tight">
          表示中の階層では未適用 (下位の絞り込みは上位に反映しません)
        </div>
      )}
    </>
  );
}
